<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { ObjectLink } from '@vp/shared-types';
import { live, ui } from '../editor/bridge';
import { bookmarks, followLink, linkLabel, linkPick, safeUrl, setLink } from '../editor/bookmarks';
import { keepCanvasSelection } from '../editor/dismiss';
import { t } from '../i18n';
import { useApp } from '../stores/app';
import Icon from './Icon.vue';

/** The link of an object: to a saved view, another object (picked on the canvas), another canvas or a web address. */
const props = defineProps<{ id: string }>();
const emit = defineEmits<{ close: [] }>();
const app = useApp();

const obj = computed(() => {
  void ui.docRev;
  return live.editor?.doc.get(props.id) ?? null;
});
const link = computed<ObjectLink | null>(() => (obj.value?.link as ObjectLink | undefined) ?? null);
const kind = ref<ObjectLink['kind']>(link.value?.kind ?? 'url');
const url = ref(link.value?.kind === 'url' ? link.value.target : '');
const urlError = ref(false);
const urlInput = ref<HTMLInputElement | null>(null);
watch(link, (l) => {
  if (l) kind.value = l.kind;
});
// the address field is ready to type into
watch(
  kind,
  (k) => {
    if (k === 'url') void nextTick(() => urlInput.value?.focus());
  },
  { immediate: true },
);
const otherCanvases = computed(() => app.canvases.filter((c) => c.id !== app.active));

function choose(l: ObjectLink): void {
  setLink(props.id, l);
}
function saveUrl(): void {
  const u = safeUrl(url.value);
  urlError.value = !u;
  if (u) {
    url.value = u;
    choose({ kind: 'url', target: u });
  }
}
function pick(): void {
  linkPick.from = props.id;
  emit('close');
}
const root = ref<HTMLElement | null>(null);
function onKey(e: KeyboardEvent): void {
  e.stopPropagation();
  if (e.key === 'Escape') emit('close');
}
/** A press outside the card closes it. The link button keeps its own toggle. On the canvas the press only closes the card. */
function onOutside(e: PointerEvent): void {
  const t = e.target as Node | null;
  if (root.value && t && root.value.contains(t)) return;
  if ((e.target as Element | null)?.closest?.('[data-act="link"]')) return;
  emit('close');
  keepCanvasSelection(e);
}
onMounted(() => window.addEventListener('pointerdown', onOutside, true));
onBeforeUnmount(() => window.removeEventListener('pointerdown', onOutside, true));
const KINDS: { kind: ObjectLink['kind']; icon: string }[] = [
  { kind: 'url', icon: 'hyperlink' },
  { kind: 'bookmark', icon: 'bookmark' },
  { kind: 'object', icon: 'select' },
  { kind: 'canvas', icon: 'grid' },
];
const iconOf = (k: ObjectLink['kind']) => KINDS.find((x) => x.kind === k)!.icon;
const isCurrent = (k: ObjectLink['kind'], target: string) => link.value?.kind === k && link.value.target === target;
</script>

<template>
  <div v-if="obj" ref="root" class="float card link-card glass" :class="{ low: ui.barBelow }" role="dialog" :aria-label="t('links.title')" data-vp-dismiss @pointerdown.stop @keydown="onKey">
    <div class="top">
      <div class="seg-pills kinds" role="radiogroup" :aria-label="t('links.title')">
        <button v-for="k in KINDS" :key="k.kind" role="radio" :aria-checked="kind === k.kind" :class="{ active: kind === k.kind }" :data-kind="k.kind" :title="t('links.kind.' + k.kind)" @click="kind = k.kind">
          <Icon :name="k.icon" :size="13" /><span>{{ t('links.kind.' + k.kind) }}</span>
        </button>
      </div>
      <button class="icon-btn" :aria-label="t('common.close')" @click="emit('close')"><Icon name="close" :size="14" /></button>
    </div>

    <form v-if="kind === 'url'" class="url" :class="{ bad: urlError }" @submit.prevent="saveUrl">
      <input ref="urlInput" v-model="url" type="text" inputmode="url" placeholder="https://…" :aria-invalid="urlError" :aria-label="t('links.kind.url')" data-field="url" @input="urlError = false" />
      <button class="ok" type="submit" :aria-label="t('common.save')" :title="t('common.save')" :disabled="!url.trim()"><Icon name="check" :size="14" /></button>
    </form>
    <p v-if="kind === 'url' && urlError" class="err note">{{ t('links.badUrl') }}</p>

    <template v-else-if="kind === 'bookmark'">
      <ul v-if="bookmarks.length" class="list">
        <li v-for="b in bookmarks" :key="b.id">
          <button :class="{ active: isCurrent('bookmark', b.id) }" @click="choose({ kind: 'bookmark', target: b.id })">
            <Icon name="bookmark" :size="13" /><span class="name">{{ b.name }}</span><Icon v-if="isCurrent('bookmark', b.id)" name="check" :size="13" class="tick" />
          </button>
        </li>
      </ul>
      <p v-else class="muted note">{{ t('links.noBookmarks') }}</p>
    </template>

    <button v-else-if="kind === 'object'" class="pick" data-act="pick-object" @click="pick"><Icon name="select" :size="14" />{{ t('links.pick') }}</button>

    <template v-else-if="kind === 'canvas'">
      <ul v-if="otherCanvases.length" class="list">
        <li v-for="c in otherCanvases" :key="c.id">
          <button :class="{ active: isCurrent('canvas', c.id) }" @click="choose({ kind: 'canvas', target: c.id })">
            <Icon name="grid" :size="13" /><span class="name">{{ c.name || t('home.untitled') }}</span><Icon v-if="isCurrent('canvas', c.id)" name="check" :size="13" class="tick" />
          </button>
        </li>
      </ul>
      <p v-else class="muted note">{{ t('links.noCanvases') }}</p>
    </template>

    <div v-if="link" class="current">
      <Icon :name="iconOf(link.kind)" :size="13" class="cur-ico" />
      <span class="label" :title="linkLabel(link)">{{ linkLabel(link) }}</span>
      <button class="icon-btn" data-act="follow-link" :aria-label="t('links.follow')" :title="t('links.follow')" @click="followLink(link)"><Icon name="external" :size="14" /></button>
      <button class="icon-btn" data-act="remove-link" :aria-label="t('links.remove')" :title="t('links.remove')" @click="setLink(id, null)"><Icon name="trash" :size="14" /></button>
    </div>
  </div>
</template>

<style scoped>
.link-card {
  top: 64px;
  left: 50%;
  transform: translateX(-50%);
  width: min(340px, calc(100% - 24px));
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 24;
}
/* under the properties bar when it moved below the top pills */
.link-card.low {
  top: 116px;
}
.top {
  display: flex;
  align-items: center;
  gap: 4px;
}
.kinds {
  flex: 1;
  display: flex;
}
/* the chosen kind grows to show its name; the rest stay icon-only */
.kinds button {
  flex: 0 0 34px;
  width: 34px;
  max-width: 34px;
  height: 28px;
  padding: 0;
  font-size: 12px;
  gap: 4px;
  justify-content: center;
  min-width: 0;
  overflow: hidden;
  transition:
    flex-grow 0.28s cubic-bezier(0.22, 1, 0.36, 1),
    flex-basis 0.28s cubic-bezier(0.22, 1, 0.36, 1),
    max-width 0.28s cubic-bezier(0.22, 1, 0.36, 1),
    width 0.28s cubic-bezier(0.22, 1, 0.36, 1),
    padding 0.28s cubic-bezier(0.22, 1, 0.36, 1),
    background 0.2s ease;
}
.kinds button.active {
  flex: 1 0 34px;
  width: auto;
  max-width: 220px;
  padding: 0 10px;
}
.kinds button span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 0;
  opacity: 0;
  transition:
    max-width 0.28s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.18s ease;
}
.kinds button.active span {
  max-width: 10em;
  opacity: 1;
}
@media (prefers-reduced-motion: reduce) {
  .kinds button,
  .kinds button span {
    transition: none;
  }
}
.url {
  display: flex;
  align-items: center;
  height: 34px;
  padding: 0 3px 0 12px;
  border: 1px solid var(--border);
  border-radius: 999px;
  background: var(--bg);
}
.url:focus-within {
  border-color: var(--accent);
}
.url.bad {
  border-color: var(--danger);
}
.url input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: var(--text);
  font: inherit;
  font-size: 13px;
}
.ok {
  flex: none;
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  border: none;
  border-radius: 50%;
  background: var(--accent);
  color: var(--on-accent, #fff);
  cursor: pointer;
}
.ok:disabled {
  opacity: 0.35;
  cursor: default;
}
.list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 180px;
  overflow: auto;
  display: grid;
  gap: 2px;
}
.list button {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 30px;
  padding: 0 10px;
  border: none;
  border-radius: 10px;
  background: none;
  color: var(--text);
  font: inherit;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
}
.list button:hover {
  background: var(--panel-2);
}
.list button.active {
  background: var(--accent-soft);
}
.name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tick {
  color: var(--accent);
}
.pick {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 34px;
  border: 1px dashed var(--border-strong, var(--border));
  border-radius: 999px;
  background: none;
  color: var(--text);
  font: inherit;
  font-size: 13px;
  cursor: pointer;
}
.pick:hover {
  border-color: var(--accent);
  color: var(--accent);
}
.current {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 4px 4px 10px;
  border-radius: 999px;
  background: var(--accent-soft);
  font-size: 12.5px;
}
.cur-ico {
  flex: none;
  color: var(--accent);
}
.label {
  flex: 1;
  min-width: 0;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.current .icon-btn {
  width: 26px;
  height: 26px;
}
.note {
  margin: 0 4px;
  font-size: 12px;
}
.err {
  color: var(--danger);
}
</style>
